import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppError } from '../common/app-error';
import { BadInput } from '../common/bad-input';
import { NotFoundError } from '../common/not-found-error';

@Injectable()
export class DataService<T = any> {
  private _createSubject$ = new Subject();
  public create$ = this._createSubject$.asObservable();

  private _deleteSubject$ = new Subject();
  public delete$ = this._deleteSubject$.asObservable();

  private _updateSubject$ = new Subject();
  private _archiveSubject$ = new Subject();
  public update$ = this._updateSubject$.asObservable();
  public archive$ = this._archiveSubject$.asObservable();

  constructor(protected url: string, protected http: HttpClient) {
  }

  getAll(): Observable<T[]> {
    return this.http.get(this.url).pipe(
      map((response: T[]) => response),
      catchError(this.handleError)
    );
  }

  create(resource) {
    return this.http.post(this.url, resource).pipe(
      map((response: T) => response),
      tap((response: T) => this._createSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  update(resource) {
    return this.http.put(this.url + resource.id, resource).pipe(
      map((response: T) => response),
      tap((response: T) => this._updateSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  updaterequest(resource) {
    return this.http.put(this.url + resource.id+'/update', resource).pipe(
      map((response: T) => response),
      tap((response: T) => this._updateSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  archive(resource) {
    return this.http.post(this.url + resource + '/archive', { isArchived: true }).pipe(
      map((response: T) => response),
      tap((response: T) => this._archiveSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  unarchive(resource) {
    return this.http.put(this.url + resource + '/unarchive', { isArchived: false }).pipe(
      map((response: T) => response),
      tap((response: T) => this._archiveSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  getById(id: string) {
    return this.http.get(this.url + id).pipe(
      map((response: T) => response),
      catchError(this.handleError)
    );
  }

  delete(id) {
    return this.http.delete(this.url + id).pipe(
      map((response: T) => response),
      tap((response: T) => this._deleteSubject$.next(response)),
      catchError(this.handleError)
    );
  }

  move(id, nodeFrom, nodeTarget, newPosition) {
    const datas = { id, nodeFrom, nodeTarget, newPosition };

    return this.http.post(this.url + 'move', datas).toPromise();
  }

  search(searchTerm: string) {

    const datas = { searchTerm: searchTerm };
    return this.http.post(this.url + 'search', datas).toPromise();

  }

  getArchivedContent() {
    return this.http.get(this.url + 'getarchived').toPromise();

  }

  createContentAndWorkspace(data: any) {
    return this.http.post(this.url + 'content-service', data).toPromise();
  }

  duplicate(contentId: string, title: string) {
    return this.http.post(this.url + 'duplicate', { contentId, title }).toPromise();
  }

  protected handleError(error: Response) {
    console.error(error);
    if (error.status === 400) {
      return throwError(new BadInput(error));
    }

    if (error.status === 404) {
      return throwError(new NotFoundError());
    }

    return throwError(new AppError(error));
  }

  verifyNumber(phone: string) {
    return this.http.post(this.url+'verify' , { phone }).toPromise();
  }

  verifyCode(code: string, sid: string) {
    return this.http.post(this.url+ 'code', { code, sid }).toPromise();
  }
}
